import { AxiosResponse } from "axios";
import React, { createContext, useContext, useEffect, useState } from "react";
import { dateRanges, TransactionsResponse } from "../types/Api";
import {
  filterTokens,
  getDataPeriod,
  getDataPeriodTotals,
  getDateLabel,
  getDateString,
  getMonthNumber,
  getMonthsSinceJanuary,
  getPreviousMonthsRange,
  getYearMonthsAgo,
  splitByPreviousMonth,
  useTransactionsRequest,
} from "../util";
import { AuthContext } from "./AuthContext";
import { FilterContext } from "./FilterContext";

const defaultTotals = {
  last7Days: { swaps: {}, dashboards: {} },
  last30Days: { swaps: {}, dashboards: {} },
  thisMonth: { swaps: {}, dashboards: {} },
  previousMonths: [],
};

export const TransactionsContext = createContext<TransactionsContextData>({
  transactions: { swaps: {}, dashboards: {} },
  loading: true,
  transactionTotals: defaultTotals,
  getTotalTransactions: () => {},
  totalsLoading: false,
  totalsError: false,
});

export interface TransactionTotals {
  last7Days: TransactionsResponse;
  last30Days: TransactionsResponse;
  thisMonth: TransactionsResponse;
  previousMonths: TransactionsResponse[];
}

export interface TransactionsContextData {
  transactions: TransactionsResponse;
  loading: boolean;
  transactionTotals: TransactionTotals;
  getTotalTransactions: Function;
  totalsLoading: boolean;
  totalsError: boolean;
}

// Map each user to their own lowering factor (0..1). Not listed => untouched.
export const LOWER_FACTORS_BY_USER: Record<string, number> = {
  "685ec82302c02c357cd4f868": 0.8540807347,
  "66b3c5f752948d0b39bdf513": 0.8540807347,
};

type TxPoint = { date: string; transactions: number; amount: number };

/**
 * Lower all dashboard series for a given user based on LOWER_FACTORS_BY_USER.
 * If the user has no factor (or factor === 1), returns the data unchanged.
 */
export function transformAllDashboardsTransactionDataForUser(
  data: TransactionsResponse,
  userId?: string,
  factors: Record<string, number> = LOWER_FACTORS_BY_USER
): TransactionsResponse {
  if (!data?.dashboards) return data;

  const factor = userId ? factors[userId] : undefined;
  if (!factor || factor === 1) return data;

  const nextDash: Record<string, Array<TxPoint>> = {};

  for (const [name, series] of Object.entries(data.dashboards)) {
    if (Array.isArray(series)) {
      nextDash[name] = (series as TxPoint[]).map((p) => ({
        ...p,
        // transactions: Math.max(
        //   0,
        //   Math.round((Number(p.transactions) || 0) * factor)
        // ),
        amount: Math.floor(Math.max(0, (Number(p.amount) || 0) * factor)),
      }));
    } else {
      // keep any non-array aggregates as-is
      nextDash[name] = series as any;
    }
  }

  return { ...data, dashboards: nextDash };
}

export const TransactionsContextWrapper: React.FC = ({ children }) => {
  const transactionsRequest = useTransactionsRequest();
  const [transactions, setTransactions] = useState<
    TransactionsResponse | undefined
  >();
  const [transactionTotals, setTransactionTotals] =
    useState<TransactionTotals>();
  const [fetching, setFetching] = useState(false);
  const [totalsFetching, setTotalsFetching] = useState(false);
  const [totalsError, setTotalsError] = useState(false);

  const { loggedIn, user } = useContext(AuthContext);
  const { dateUnit, startDate, endDate } = useContext(FilterContext);

  useEffect(() => {
    let isSubscribed = true;

    if (!loggedIn || !user?.isEmailVerified || user?.requiresPasswordChange)
      return;

    setFetching(true);
    transactionsRequest
      .sendRequest({
        startDate,
        endDate,
        unit: dateUnit,
      })
      .then((res) => {
        if (!isSubscribed) return;
        let castedRes = res as AxiosResponse<TransactionsResponse>;
        if (castedRes.data)
          setTransactions(
            transformAllDashboardsTransactionDataForUser(
              castedRes.data,
              user?.id
            )
          );
      })
      .finally(() => {
        if (isSubscribed) setFetching(false);
      });

    return () => {
      isSubscribed = false;
    };
  }, [
    loggedIn,
    dateUnit,
    endDate,
    startDate,
    user?.id,
    user?.isEmailVerified,
    user?.requiresPasswordChange,
  ]);

  const getTotalTransactions = () => {
    setTotalsFetching(true);
    let promises: Promise<any>[] = [];

    // First promise for last 30 days
    promises.push(
      transactionsRequest
        .sendRequest({
          startDate: new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString(),
          unit: dateUnit,
        })
        .then((res) => {
          let castedRes = res as AxiosResponse<TransactionsResponse>;
          if (!castedRes?.data) return;
          castedRes.data = transformAllDashboardsTransactionDataForUser(
            castedRes.data,
            user?.id
          );

          setTransactionTotals(
            (prevTotals) =>
              ({
                ...prevTotals,
                last30Days: {
                  dashboards: castedRes.data.dashboards,
                  swaps: castedRes.data.swaps,
                },
                last7Days: {
                  dashboards: getDataPeriod(
                    castedRes.data.dashboards,
                    [0, 7],
                    "date"
                  ),
                  swaps: getDataPeriod(castedRes.data.swaps, [0, 7], "date"),
                },
              } as TransactionTotals)
          );
        })
    );

    // Second promise for this month
    const currentDate = new Date();
    const firstDayOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    );

    promises.push(
      transactionsRequest
        .sendRequest({
          startDate: firstDayOfMonth.toISOString(),
          unit: dateUnit,
        })
        .then((res) => {
          let castedRes = res as AxiosResponse<TransactionsResponse>;
          if (!castedRes?.data) return;
          castedRes.data = transformAllDashboardsTransactionDataForUser(
            castedRes.data,
            user?.id
          );

          setTransactionTotals(
            (prevTotals) =>
              ({
                ...prevTotals,
                thisMonth: {
                  swaps: castedRes.data.swaps,
                  dashboards: castedRes.data.dashboards,
                },
              } as TransactionTotals)
          );
        })
    );

    // Previous months: one request grouped by calendar month, split per month here
    const monthsSince = getMonthsSinceJanuary();
    if (monthsSince > 0) {
      promises.push(
        transactionsRequest
          .sendRequest({
            ...getPreviousMonthsRange(monthsSince),
            unit: "month",
          })
          .then((res) => {
            let castedRes = res as AxiosResponse<TransactionsResponse>;
            if (!castedRes?.data) return;
            castedRes.data = transformAllDashboardsTransactionDataForUser(
              castedRes.data,
              user?.id
            );

            setTransactionTotals(
              (prevTotals) =>
                ({
                  ...prevTotals,
                  previousMonths: splitByPreviousMonth(
                    castedRes.data,
                    monthsSince
                  ),
                } as TransactionTotals)
            );
          })
      );
    }

    Promise.all(promises)
      .then(() => {
        setTotalsFetching(false);
      })
      .catch((err) => {
        console.error("Error fetching transactions:", err);
        setTotalsFetching(false);
        setTotalsError(true);
      });
  };

  const TransactionsData: TransactionsContextData = {
    transactions: transactions || defaultTotals.last30Days,
    transactionTotals: transactionTotals || defaultTotals,
    loading: fetching || !transactionsRequest.fetchedAt,
    getTotalTransactions,
    totalsLoading: totalsFetching,
    totalsError,
  };

  return (
    <TransactionsContext.Provider value={TransactionsData}>
      {children}
    </TransactionsContext.Provider>
  );
};


           


